import { useState, type ReactNode } from "react";

import {
  AppDialog,
  AppDialogActions,
  DialogPrimaryButton,
  DialogSecondaryButton,
} from "@/components/ui/app-dialog";
import { DimensionField } from "@/components/ui/dimension-field";
import { FormGrid, FormGridCell, type FormGridSpan } from "@/components/ui/form-grid";
import { LookupSelectField } from "@/components/ui/lookup-select-field";
import { RichTextField } from "@/components/ui/rich-text-field";
import { TextField } from "@/components/ui/text-field";
import { TreeSelect } from "@/components/ui/tree-select";
import { MuiTypography } from "@/components/ui/mui-typography";
import {
  useCatalogLookupMenu,
  useCreateCatalogLookup,
  useDeleteCatalogLookup,
  useRenameCatalogLookup,
} from "@/hooks/catalog-admin";
import { useAddCatalogItemVariant } from "@/hooks/catalog-items";
import { useItemsError } from "@/hooks/items-error";
import { BffUserFacingError, errorMessageForDisplay } from "@/lib/errors";
import {
  addVariantFieldProblems,
  addVariantInputFromDraft,
  blankAddVariantDraft,
  type AddVariantDraft,
} from "@/lib/add-variant-form";
import type { CatalogDetailItem } from "@/lib/catalog-item-detail";
import { AddVariantPreviewCard } from "./add-variant-preview-card";

// The Add Variant dialog — the donor's `AddVariantIsland`, ported.
//
// Source: `timmy/src/domains/items/islands/AddVariantIsland/AddVariantIsland.tsx`.
// `fullWidth maxWidth='lg'`, and its body is a two-column MUI `Grid` at
// `spacing={5}`: a preview of the source item on the left (`md={4}`) and the
// form on the right (`md={8}`, its own container at `spacing={1.5}`). The field
// order below is that grid's, unchanged — including `Item Number (JDE)` and
// `Link to Item` taking full rows between two pairs of half ones.
//
// **What this component owns is arrangement**, as the Request Item dialog does:
// the rules, the messages and the payload are `@/lib/add-variant-form`, the
// preview card is its own component, and the write is a hook.
//
// Three things are this dialog's alone, and each is a decision rather than a
// layout:
//
//   - **Manufacturer and Category are inherited and locked.** They are drawn as
//     the real selects, `required` *and* `disabled`, holding the parent's value —
//     which is what the donor does — but their option lists are the parent's own
//     value and nothing else. A disabled select cannot be opened, so there is no
//     list to read, and reading manufacturers and the whole category tree to
//     display two names the dialog was handed would be two requests spent on a
//     field nobody can change. Nothing is sent either way: Python derives both
//     from the parent the path names (ADR-0005 decision 13).
//   - **A variant of an uncategorised item is refused**, with the message under
//     the locked Category select. That is the donor's dead end, ported — see
//     `addVariantFieldProblems`.
//   - **The failure snackbar is the donor's fixed sentence**, not #252's
//     tagged-message treatment that the Request Item dialog uses. #254 asks for
//     it "exactly", so it ships exactly, and the divergence between the two
//     dialogs is on the decision menu rather than settled here — it matters
//     because this form deliberately does *not* cap the model number, so a
//     50-character refusal from Python is a real case where the tagged message
//     would name the field and this one will not.
//
// **The image uploader is not here**, and that is a citation rather than a cut:
// the donor's last grid cell is `ItemImagesUploader`, and #254 rules it onto the
// images slice ("Images in this slice are schema and placeholders only",
// ADR-0003 / #238). The preview card's *hero* is a real picture, because #254
// asks that column for one by name.

/** The two lookup domains this form reads, and the donor's labels for them. */
const LOOKUP_LABELS = { finish: "Color/Finish", material: "Material" } as const;

export interface AddVariantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The item the variant is being added to — the dialog's whole inherited half.
   *
   * `null` while the details page's read is in flight, which the page's action
   * menu already prevents (no item, no menu); the dialog draws nothing rather
   * than a form with two empty locked fields.
   */
  parent: CatalogDetailItem | null;
  /** The parent's whole group, for the preview card's tile strip. */
  variants: readonly CatalogDetailItem[];
  /** Called with the new variant's id once the write has landed. */
  onAdded: (variantId: number) => void;
}

export function AddVariantDialog({
  open,
  onOpenChange,
  parent,
  variants,
  onAdded,
}: AddVariantDialogProps) {
  /**
   * The draft, which fields have been touched, and **which draft this is** —
   * one state, because all three are put away together and the last of them is
   * what a late answer checks itself against.
   *
   * The lookup selects write back *after an await*: adding a finish from inside
   * the menu resolves and then selects what it created. The select refuses its
   * own late write when its *menu* closed first
   * (`lookup-select-field.tsx:211-219`), and that covers every dismissal a
   * typist can click — Cancel, the X, Escape and the overlay all sit outside an
   * open menu. What it does not cover is the **page** putting the draft away:
   * the item details route closes this dialog when the URL moves to another
   * item, no pointer event involved and no menu closed, and a create still in
   * flight then lands on the blank draft that replaced it.
   *
   * `era` rides *inside* the state rather than in a ref so the comparison
   * happens against the value at update time, in a functional update, with
   * nothing written during a render.
   */
  const [form, setForm] = useState<{
    draft: AddVariantDraft;
    edited: ReadonlySet<keyof AddVariantDraft>;
    era: number;
  }>(() => ({ draft: blankAddVariantDraft(), edited: new Set(), era: 0 }));
  const { draft, edited } = form;
  const [showProblems, setShowProblems] = useState(false);
  /**
   * Which variant the preview card is showing.
   *
   * The donor's `ItemDetails` holds exactly this, and it is the dialog's own
   * rather than the page's: clicking a tile in here changes the picture beside
   * the form and nothing about the page underneath
   * (`item-details.tsx:36-45`).
   */
  const [previewId, setPreviewId] = useState<number | null>(null);

  const finishes = useCatalogLookupMenu("finish");
  const materials = useCatalogLookupMenu("material");
  const addVariant = useAddCatalogItemVariant();
  const createLookup = useCreateCatalogLookup();
  const renameLookup = useRenameCatalogLookup();
  const deleteLookup = useDeleteCatalogLookup();

  const blankDraft = () => {
    // A new `era`, so every setter made against the draft being discarded now
    // refuses to write. `edited` is emptied in the same breath: a field marked
    // touched by a late answer would greet the next opening with a
    // required-field message under an empty box.
    setForm((current) => ({
      draft: blankAddVariantDraft(),
      edited: new Set(),
      era: current.era + 1,
    }));
    // The donor resets its form on every *open* (`useVariantForm`'s `opening`
    // effect). Doing it on the way out instead keeps it to a single render and
    // keeps a reopened dialog from flashing the previous draft — the same
    // choice the Request Item dialog made, for the same reason.
    setShowProblems(false);
    setPreviewId(null);
    // **Never reset a write that is still in flight.** Cancel, the X, Escape
    // and the backdrop all stay live while submitting — the donor does not
    // disable them either (`AddVariantIsland.tsx:187-189`) — so this path is
    // reachable with a payload already on the wire, and `reset()` there
    // detaches the observer without cancelling anything: the variant is still
    // created, but the per-call `onSuccess` no longer runs, so the page is never
    // told and a reopened dialog offers a second submit of the same draft.
    //
    // Leaving it alone is also the donor's behavior rather than a divergence.
    // Its success observer lives on the island and fires on the dispatched
    // event, not on the dialog being open (`observers.ts:152-188`), so in modsy
    // a cancelled-then-successful add still switches the page onto the new
    // variant. Here the mutation simply settles as it would have: `onSuccess`
    // closes an already-closed dialog, hands the page the id, and the next
    // close reaches this line with nothing pending and clears it properly.
    if (!addVariant.isPending) addVariant.reset();
  };

  /**
   * The draft is put away when `open` goes false — **however it went false.**
   *
   * Doing this on the prop rather than inside the dismissal handler is the
   * point. This dialog stays mounted while closed (it needs an element to
   * animate out of), so its draft outlives any close, and the page above can
   * close it without ever calling `onOpenChange`: the item details route closes
   * it when the URL moves to another item, which the browser's Back button
   * reaches while the dialog is open. Resetting only in `changeOpen` left that
   * path holding the previous item's typed values against a *new* `parent`, so
   * submitting created a variant of the wrong item.
   *
   * Reset during render, not in an effect — an effect commits a frame late, and
   * for the fraction of a second before it runs a reopened dialog shows the old
   * draft. This is React's own answer for state that has to follow a changing
   * prop, and the item details route resets its variant selection the same way.
   */
  const [openSeen, setOpenSeen] = useState(open);
  if (openSeen !== open) {
    setOpenSeen(open);
    if (!open) blankDraft();
  }

  const changeOpen = (next: boolean) => {
    // No reset here: `next === false` flows back through `open` and the
    // transition above does it, so there is exactly one place the draft is put
    // away and it cannot be bypassed.
    onOpenChange(next);
  };

  // Deliberately not memoized: each render's setters capture *that* render's
  // era, which is the whole mechanism. A stable identity would hand every draft
  // the same closure and there would be nothing to compare against.
  const set = <K extends keyof AddVariantDraft>(key: K) => {
    // Captured where the setter is *made*, during a render of this draft.
    const era = form.era;
    return (next: AddVariantDraft[K]) => {
      setForm((current) =>
        // A write that lands after this draft was put away belongs to a form
        // that no longer exists.
        current.era !== era
          ? current
          : {
              ...current,
              draft: { ...current.draft, [key]: next },
              edited: current.edited.has(key) ? current.edited : new Set([...current.edited, key]),
            },
      );
    };
  };

  // The donor's fields disable themselves while the form is submitting
  // (`InputField.tsx`: `disabled={disabled || isSubmitting}`) — a draft edited
  // after the payload has gone would leave the typist reading an answer about
  // values they can no longer see.
  const busy = addVariant.isPending;

  const problems = parent === null ? {} : addVariantFieldProblems(draft, parent);
  const problem = (key: keyof AddVariantDraft) =>
    showProblems || edited.has(key) ? problems[key] : undefined;

  // The donor's `ItemsError`, through the shared hook — which is where its
  // corner, weight, inset and ten seconds are stated.
  useItemsError(addVariant.isError, submitErrorMessage(addVariant.error));

  if (parent === null) return null;

  const shown = variants.find((variant) => variant.id === previewId) ?? parent;

  /** The donor's delete-by-clearing-the-name, which the select reaches when a
   * rename is submitted empty — and, on the owner's D3 ruling, only after the
   * select has asked. See the note in `lookup-select-field.tsx`. */
  const deleteOption = (kind: "finish" | "material") => async (id: number) => {
    await deleteLookup.mutateAsync({ kind, id });
  };

  const submit = () => {
    // The gate, rather than the disabled button being the gate: the form has an
    // implicit submit path (Enter in a text field) that the button's `disabled`
    // does not cover, and a second payload would create a second variant.
    if (busy) return;
    setShowProblems(true);
    if (Object.keys(problems).length > 0) return;
    addVariant.mutate(addVariantInputFromDraft(parent.id, draft), {
      onSuccess: (result) => {
        // Close first, then hand the page the new variant — the donor's order
        // (`onClose()` after the success event is dispatched, and the observer
        // switches the page's displayed variant).
        changeOpen(false);
        onAdded(result.item.id);
      },
    });
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={changeOpen}
      title="Add Variant"
      size="lg"
      // `sx={{ px: 10 }}` on the shared `Dialog`, which forwards `sx` to the MUI
      // Dialog *root* (`react-ui/dialog/dialog.tsx:131`) — 80px a side on top of
      // MUI's own 32px margins, so the paper is `min(1200, 100vw - 224)`. Unique
      // to this island: `RequestItemIsland` has no such `sx` and keeps the
      // template's `min(1200, 100vw - 64)`.
      className="w-[calc(100%-14rem)]"
      actions={
        <AppDialogActions>
          <DialogSecondaryButton onClick={() => changeOpen(false)}>Cancel</DialogSecondaryButton>
          {/* The donor's `loading={isLoading}` — MUI's loading button pins a
              20px indicator to the left edge and disables itself. */}
          <DialogPrimaryButton type="submit" form={FORM_ID} loading={busy}>
            Add Variant
          </DialogPrimaryButton>
        </AppDialogActions>
      }
    >
      {/* `Grid component='form' container sx={{ pt: 1 }} spacing={5}` — two
          columns 40px apart. */}
      <FormGrid spacing={5} asChild>
        <form
          id={FORM_ID}
          data-slot="add-variant-form"
          noValidate
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          className="pt-2"
        >
          <div className="col-span-12 md:col-span-4">
            <AddVariantPreviewCard
              baseItemId={parent.id}
              item={shown}
              variants={variants}
              onSelectVariant={setPreviewId}
            />
          </div>

          {/* `Grid item xs={12} md={8} container spacing={1.5}` — the form's own
            12-column grid, 12px apart. */}
          <FormGrid spacing={1.5} className="col-span-12 md:col-span-8">
            {/* Locked, and holding the parent's value as their only option: a
              disabled select has no menu to open, so there is no list to read. */}
            <Cell field="manufacturerId" span="half">
              <LookupSelectField
                label="Manufacturer"
                required
                disabled
                // `isLocked` is the server's "may this row be edited" answer, and
                // the honest value for a field nobody can open: the select's
                // editable affordances are not passed here either way.
                options={[{ ...parent.manufacturer, isLocked: true }]}
                value={parent.manufacturer.id}
                onChange={() => {}}
              />
            </Cell>

            <Cell field="categoryId" span="half">
              <TreeSelect
                label="Category"
                required
                disabled
                allowEmpty={false}
                size="medium"
                nodes={
                  parent.category
                    ? [
                        {
                          id: parent.category.id,
                          name: parent.category.name,
                          depth: 0,
                          children: [],
                        },
                      ]
                    : []
                }
                emptyLabel=""
                searchPlaceholder="Search categories"
                searchTerm=""
                onSearchTermChange={() => {}}
                value={parent.category?.id ?? null}
                onChange={() => {}}
                // The donor's dead end: a locked required field with nothing in
                // it, refused on submit with no way for the typist to fix it here.
                error={showProblems ? problems.categoryId : undefined}
              />
            </Cell>

            {/* `Typography variant='body1' sx={{ my: 1 }}` on its own full row. */}
            <Cell field="heading" span="full">
              <MuiTypography variant="body1" color="primary" className="my-2">
                Variant Details
              </MuiTypography>
            </Cell>

            <Cell field="name" span="half">
              <TextField
                disabled={busy}
                label="Item Name"
                required
                multiline
                // The donor's `focusFirstInput` on the dialog's transition: the
                // two fields above it are disabled and so unfocusable, which makes
                // this the first input either way.
                autoFocus
                value={draft.name}
                onChange={set("name")}
                error={problem("name")}
              />
            </Cell>

            <Cell field="modelNumber" span="half">
              {/* No length rule, deliberately: Request Item passes
                `maxLength: 50` here and this form passes nothing
                (`AddVariantIsland.tsx:108-114`). Python still refuses a longer
                one. */}
              <TextField
                disabled={busy}
                label="Model Number"
                multiline
                value={draft.modelNumber}
                onChange={set("modelNumber")}
              />
            </Cell>

            <Cell field="jdeItemNumber" span="full">
              <TextField
                disabled={busy}
                label="Item Number (JDE)"
                value={draft.jdeItemNumber}
                onChange={set("jdeItemNumber")}
              />
            </Cell>

            <Cell field="url" span="full">
              <TextField
                disabled={busy}
                label="Link to Item"
                value={draft.url}
                onChange={set("url")}
                error={problem("url")}
              />
            </Cell>

            <Cell field="description" span="full">
              <RichTextField
                disabled={busy}
                label="Description"
                value={draft.description}
                onChange={set("description")}
              />
            </Cell>

            {/* **Editable, unlike the Request Item dialog's two.** Neither select
              here passes `isEditable`, and `FinishesCustomSelect` /
              `MaterialsCustomSelect` default it to `true`
              (`FinishesCustomSelect.tsx:18`, `MaterialsCustomSelect.tsx:17`);
              `RequestItemIsland.tsx:139,142` passes `isEditable={false}` on the
              same two. So a finish or a material may be renamed — and, by
              clearing its name, deleted — from *this* dropdown and not from
              that one. A deliberate contrast in the source, carried. */}
            <Cell field="finishId" span="half">
              <LookupSelectField
                disabled={busy}
                label={LOOKUP_LABELS.finish}
                required
                editable
                {...finishes}
                value={draft.finishId}
                onChange={set("finishId")}
                error={problem("finishId")}
                onCreateOption={(name) => createLookup.mutateAsync({ kind: "finish", name })}
                onRenameOption={(id, name) =>
                  renameLookup.mutateAsync({ kind: "finish", id, name })
                }
                onDeleteOption={deleteOption("finish")}
              />
            </Cell>

            <Cell field="materialId" span="half">
              <LookupSelectField
                disabled={busy}
                label={LOOKUP_LABELS.material}
                editable
                {...materials}
                value={draft.materialId}
                onChange={set("materialId")}
                onCreateOption={(name) => createLookup.mutateAsync({ kind: "material", name })}
                onRenameOption={(id, name) =>
                  renameLookup.mutateAsync({ kind: "material", id, name })
                }
                onDeleteOption={deleteOption("material")}
              />
            </Cell>

            {/* `Width` / `Height` / `Depth` — **without** the "(inches)" the
              Request Item dialog's three carry. Two forms, two label sets, both
              the donor's (`AddVariantIsland.tsx:158-174` against
              `RequestItemIsland.tsx:146-166`).
              The order is the form's, and it is deliberately not the
              width/depth/height the variant chips join their label in. */}
            <Cell field="dimensionWidth" span="third">
              <DimensionField
                disabled={busy}
                label="Width"
                value={draft.dimensionWidth}
                onChange={set("dimensionWidth")}
                error={problem("dimensionWidth")}
              />
            </Cell>

            <Cell field="dimensionHeight" span="third">
              <DimensionField
                disabled={busy}
                label="Height"
                value={draft.dimensionHeight}
                onChange={set("dimensionHeight")}
                error={problem("dimensionHeight")}
              />
            </Cell>

            <Cell field="dimensionDepth" span="third">
              <DimensionField
                disabled={busy}
                label="Depth"
                value={draft.dimensionDepth}
                onChange={set("dimensionDepth")}
                error={problem("dimensionDepth")}
              />
            </Cell>
          </FormGrid>
        </form>
      </FormGrid>
    </AppDialog>
  );
}

/** The form's id, so the submit button can sit in the dialog's action row and
 * still submit the form — the donor's `form='create-asset-variant-dialog-form'`. */
const FORM_ID = "add-variant-form";

/** The donor's fixed sentence for a refused submit (`ItemsError`'s `error`). */
const ADD_VARIANT_ERROR = "Error adding variant. Click on the 'Add Variant' button to try again.";

/**
 * What a refused submit says — an owner ruling (#254 decision menu, D4 = b).
 *
 * The donor says one sentence for every failure, and this form used to as well.
 * The trouble is what the form *accepts*: it passes no rule for Model Number,
 * because the donor passes none (`AddVariantIsland.tsx:108-114`, where its own
 * Request Item form caps at 50), and it submits a whitespace-only name. Python
 * refuses both, so a typist could be told "Error adding variant" for a value the
 * form had raised no objection to and given nothing to act on.
 *
 * Python's refusal already names the field — "Model Number must be 50
 * characters or fewer." — and raises it as `invalid-input:catalog-item`
 * (`use_cases/catalog/add_variant.py`, `item_patch.py`'s `_text`/`_url`), a tag
 * the scrub allowlist passes through intact. So the fix is to say what the
 * server said, which keeps the server the only authority on the rule and
 * invents no client-side cap the donor does not have. Identical to what the
 * Request Item dialog has done since #252 (`request-item-dialog.tsx`).
 *
 * Anything *not* tagged has already been scrubbed to a generic message by the
 * global middleware, and there the donor's sentence is the better thing to say,
 * because it tells the typist what to do next.
 */
function submitErrorMessage(error: unknown): string {
  if (error instanceof BffUserFacingError) return errorMessageForDisplay(error);
  return ADD_VARIANT_ERROR;
}

/** One grid cell, on the shared 12-column form grid. */
function Cell({
  field,
  span,
  children,
}: {
  field: keyof AddVariantDraft | "manufacturerId" | "categoryId" | "heading";
  span: FormGridSpan;
  children: ReactNode;
}) {
  return (
    <FormGridCell slot="add-variant-field" field={field} span={span}>
      {children}
    </FormGridCell>
  );
}
